/**
 * Propriétés analytics marketing localisées (PostHog).
 * À appeler depuis LocaleRouteSync / PublicLayout sur changement de pathname.
 */

import { getLocaleFromPath, isBlogPath, isMarketingPath, type MarketingLocale } from '../config/localeRoutes';
import {
  getMarketFromPath,
  getMarketMetierSlugFromPath,
  type MarketingMarketCode,
} from '../config/marketRoutes';

export type MarketingSurface = 'landing' | 'metier' | 'blog' | 'features' | 'pricing' | 'market_hub' | 'other_public';

export function resolveMarketingLocale(pathname: string, i18nLanguage?: string): MarketingLocale {
  const fromPath = getLocaleFromPath(pathname);
  if (fromPath) return fromPath;
  const lang = (i18nLanguage || 'fr').split('-')[0];
  if (lang === 'es' || lang === 'en' || lang === 'de' || lang === 'it') return lang;
  return 'fr';
}

export function resolveMarketingSurface(pathname: string): MarketingSurface {
  if (isBlogPath(pathname)) return 'blog';
  const market = getMarketFromPath(pathname);
  if (market) {
    // /be, /be/nl, /nl = hub ; /be/{slug}, /be/nl/{slug}, /nl/{slug} = métier
    return getMarketMetierSlugFromPath(pathname) ? 'metier' : 'market_hub';
  }
  const stripped = pathname.replace(/^\/(en|es|de|it)(?=\/|$)/, '') || '/';
  if (stripped === '/' || stripped === '/v2') return 'landing';
  if (stripped.startsWith('/fonctionnalites')) return 'features';
  if (stripped === '/pricing' || stripped.startsWith('/offre')) return 'pricing';
  if (
    /educateur|promeneur|pet-sitter|pension|comportementaliste|toiletteur|dog-trainer|dog-walker|dog-boarding|dog-behaviorist|dog-groomer|educador|paseador|cuidador|residencia|etologo|peluqueria|hundetrainer|hundeausfuehrer|tiersitter|hundepension|verhaltensberater|hundefriseur|addestratore|passeggiatore|pensione|toelettatore/i.test(
      stripped
    )
  ) {
    return 'metier';
  }
  if (isMarketingPath(pathname)) return 'other_public';
  return 'other_public';
}

export function buildLocaleMarketingProperties(
  pathname: string,
  i18nLanguage?: string
): {
  locale: MarketingLocale;
  marketing_surface: MarketingSurface;
  pathname: string;
  market?: MarketingMarketCode;
} {
  const market = getMarketFromPath(pathname) ?? undefined;
  return {
    locale: resolveMarketingLocale(pathname, i18nLanguage),
    marketing_surface: resolveMarketingSurface(pathname),
    pathname,
    ...(market ? { market } : {}),
  };
}
